import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Flex,
  KeyCell,
  KeyValueRow,
  KeyValueTable,
  ValueCell,
} from '@amzn/storm-ui';
import { useQuery } from '@tanstack/react-query';

import { ErrorDisplay } from '../../../Shared/components/ErrorDisplay/ErrorDisplay';
import { UserEndpointQueryKeys } from '../../../Shared/constants/user/endpoints';
import { ApiError } from '../../../Shared/interfaces/ApiError';
import { ApiResponseKeys } from '../../../Shared/interfaces/ApiResponse';
import { UserInterface } from '../../../Shared/interfaces/user/UserInterfaces';
import useApiClient from '../../../Shared/services/api';
import { getFormattedDate } from '../../../Shared/utils/helpers';
import { getUserById } from '../../../Shared/services/user/userService';
import { capitalizeString } from '../../../Shared/utils/stringFunctions';
import { CustomCard } from '../../../UiComponentsLibrary/CustomCard';
import CustomSpinner from '../../../UiComponentsLibrary/CustomSpinner';
import { CustomButton } from '../../../UiComponentsLibrary/CustomButton';
import { CustomButtonColor } from '../../../UiComponentsLibrary/CustomButton/constants/CustomButtonColor';
import {
  USER_TABLE_HEADERS,
  DATE_FIELDS,
  PRIVILEGED_MODE_FIELD,
} from '../../constants/userTableConstants';

interface GetUserByIdResponse extends ApiResponseKeys {
  user: UserInterface;
}

const formatValue = (
  key: string,
  value: any,
  t: (key: string) => string,
) => {
  if (key === PRIVILEGED_MODE_FIELD) {
    return t(value ? 'privileged-mode-on' : 'privileged-mode-off');
  }
  if (value == null) return '-';
  if (DATE_FIELDS.includes(key as (typeof DATE_FIELDS)[number])) {
    return getFormattedDate(value);
  }
  return value;
};

const ViewUser = ({
  userId,
  isViewUser,
  setIsViewUser,
}: {
  userId: string;
  isViewUser: boolean;
  setIsViewUser: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { t } = useTranslation();
  const api = useApiClient();
  const {
    error,
    isLoading,
    isError,
    data: userData,
  } = useQuery<GetUserByIdResponse, ApiError>({
    queryKey: [UserEndpointQueryKeys.GET_SPECIFIC_USER, userId],
    queryFn: () => getUserById(api, { userId }),
    enabled: !!userId,
  });

  const user = userData?.user;
  const errorDisplayProps = {
    error: (error || {
      errorCode: 'unknown-error',
      statusCode: 500,
      message: t('view-user-error'),
    }) as ApiError,
    customMessage: !error ? t('view-user-error') : undefined,
  };

  return (
    <CustomCard title={t('view-user-label')}>
      <Flex direction="column" rowGap="small">
        {isLoading ? (
          <div id="view-user-spinner">
            <CustomSpinner />
          </div>
        ) : isError || !user ? (
          <div id="view-user-api-error">
            <ErrorDisplay {...errorDisplayProps} />
          </div>
        ) : (
          <KeyValueTable
            id="view-user-table"
            caption={t('view-user-table-caption')}
          >
            {Object.entries(user).map(([key, value]) => (
              <KeyValueRow id={`view-user-row-${key}`} key={key}>
                <KeyCell id={`view-user-key-${key}`}>
                  {t(
                    USER_TABLE_HEADERS[key as keyof typeof USER_TABLE_HEADERS]
                  )}
                </KeyCell>
                <ValueCell id={`view-user-value-${key}`}>
                  {formatValue(key, value, t)}
                </ValueCell>
              </KeyValueRow>
            ))}
          </KeyValueTable>
        )}
        <CustomButton
          id="view-user-back-button"
          text={t('view-user-back-button')}
          backgroundColor={CustomButtonColor.cancel}
          handleOnClickCb={() => setIsViewUser(false)}
        />
      </Flex>
    </CustomCard>
  );
};

export default ViewUser;
